import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, Input, Card } from '@nexora/design-system';
import { apiClient, ApiError } from '../../shared/api/client';

interface MfaSetupResponse {
  secret: string;
  qrCodeUrl: string;
}

export function SetupMfaPage(): JSX.Element {
  const navigate = useNavigate();

  const [step, setStep] = useState<'initial' | 'qr' | 'verify' | 'success'>('initial');
  const [setupData, setSetupData] = useState<MfaSetupResponse | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleStartSetup = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<MfaSetupResponse>(
        '/auth/mfa/setup',
      );
      setSetupData(response);
      setStep('qr');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Erro ao iniciar configurac,ao MFA');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await apiClient.post('/auth/mfa/verify-setup', {
        code: verificationCode,
        secret: setupData?.secret,
      });
      setStep('success');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Erro ao verificar codigo');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinish = (): void => {
    navigate({ to: '/', replace: true }).catch(console.error);
  };

  return (
    <div className="setup-mfa-page">
      <Card>
        <div className="setup-mfa-content">
          <h1>Configurar autenticacao em duas etapas</h1>
          <p className="subtitle">
            Aumente a segurança da sua conta com MFA
          </p>

          {step === 'initial' && (
            <div className="step-initial">
              <div className="info-box">
                <h2>Por que configurar?</h2>
                <ul>
                  <li>Protege sua conta contra acesso nao autorizado</li>
                  <li>Exige um codigo adicional ao fazer login</li>
                  <li>Compativel com Google Authenticator, Authy e similares</li>
                </ul>
              </div>

              <Button
                variant="primary"
                onClick={handleStartSetup}
                loading={isLoading}
              >
                Configurar MFA
              </Button>
            </div>
          )}

          {step === 'qr' && setupData && (
            <div className="step-qr">
              <div className="qr-container">
                <img
                  src={setupData.qrCodeUrl}
                  alt="QR Code para configurar o autenticador"
                  width={200}
                  height={200}
                />
              </div>

              <p className="instruction">
                Escaneie o QR Code com seu aplicativo autenticador ou insira
                manualmente a chave:
              </p>

              <div className="secret-key">
                <code>{setupData.secret}</code>
              </div>

              <form onSubmit={handleVerify} className="verify-form">
                <Input
                  label="Codigo de verificacao"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="000000"
                  maxLength={6}
                  aria-describedby={error ? 'mfa-error' : undefined}
                  disabled={isLoading}
                />

                {error && (
                  <div id="mfa-error" className="error-message" role="alert">
                    {error}
                  </div>
                )}

                <div className="verify-actions">
                  <Button
                    variant="outline"
                    onClick={() => setStep('initial')}
                    disabled={isLoading}
                  >
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isLoading}
                    disabled={verificationCode.length < 6}
                  >
                    Verificar e ativar
                  </Button>
                </div>
              </form>
            </div>
          )}

          {step === 'success' && (
            <div className="step-success">
              <div className="success-icon">&#10003;</div>
              <h2>MFA ativado com sucesso!</h2>
              <p>
                Agora, ao fazer login, voce precisara informar o código gerado
                pelo seu aplicativo autenticador.
              </p>
              <Button variant="primary" onClick={handleFinish}>
                Ir para o painel
              </Button>
            </div>
          )}
        </div>
      </Card>

      <style>{`
        .setup-mfa-page {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 1rem;
          background: #f2f4f6;
        }
        .setup-mfa-content {
          max-width: 480px;
          margin: 0 auto;
          text-align: center;
        }
        .setup-mfa-content h1 {
          font-size: 1.5rem;
          color: #191c1e;
          margin-bottom: 0.5rem;
        }
        .subtitle {
          color: #43474e;
          margin-bottom: 2rem;
        }
        .info-box {
          text-align: left;
          background: #f2f4f6;
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }
        .info-box h2 {
          font-size: 1rem;
          margin-bottom: 0.75rem;
        }
        .info-box ul {
          padding-left: 1.25rem;
        }
        .info-box li {
          margin-bottom: 0.5rem;
          color: #191c1e;
          font-size: 0.875rem;
        }
        .qr-container {
          margin: 1.5rem 0;
        }
        .instruction {
          color: #43474e;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }
        .secret-key {
          background: #f2f4f6;
          padding: 0.75rem;
          border-radius: 4px;
          margin-bottom: 1.5rem;
          word-break: break-all;
        }
        .secret-key code {
          font-size: 0.875rem;
          color: #191c1e;
        }
        .verify-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .verify-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
        }
        .step-success {
          text-align: center;
        }
        .success-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #d3f5d6;
          color: #2e7d32;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          margin: 0 auto 1rem;
        }
        .error-message {
          padding: 0.75rem;
          background: #ffdad6;
          border: 1px solid #ffb4ab;
          border-radius: 4px;
          color: #ba1a1a;
          font-size: 0.875rem;
          text-align: left;
        }
      `}</style>
    </div>
  );
}
